/**
 * StoriesPlayer
 * Handles circular story thumbnails + modal player.
 * Features:
 *  - Open/close modal with animation
 *  - Progress bars synced to video duration (or fallback timer)
 *  - Tap zones left/right to navigate (Instagram style)
 *  - Touch swipe — vertical (TikTok) on mobile, horizontal as fallback
 *  - Keyboard: ArrowLeft/Right, Escape
 *  - Track scroll (pill row) with prev/next arrows
 *  - Marks pills as "watched" after viewing
 *  - Respects prefers-reduced-motion
 */

class StoriesPlayer {
  constructor(sectionEl) {
    this.section  = sectionEl;
    this.uid      = sectionEl.dataset.storiesSection;
    this.modal    = document.querySelector(`[data-stories-modal="${this.uid}"]`);

    if (!this.modal) return;

    this.slides     = [...this.modal.querySelectorAll('[data-story-slide]')];
    this.fills      = [...this.modal.querySelectorAll('[data-prog-fill]')];
    this.pills      = [...this.section.querySelectorAll('[data-story-index]')];
    this.track      = this.section.querySelector('[data-stories-track]');
    this.trackPrev  = this.section.querySelector('[data-stories-track-prev]');
    this.trackNext  = this.section.querySelector('[data-stories-track-next]');
    this.progressEl = this.section.querySelector('[data-stories-progress]');
    this.progressThumb = this.section.querySelector('[data-stories-progress-thumb]');

    this.current    = 0;
    this.total      = this.slides.length;
    this.isOpen     = false;
    this.timer      = null;
    this.paused     = false;
    this.fallbackMs = parseInt(sectionEl.dataset.storyDuration || '8', 10) * 1000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this._applySettings();
    this._applyMaxVisible();
    this._bindPills();
    this._bindModal();
    this._bindTrackNav();
    this._bindSwipe();
    this._bindKeyboard();
    this._checkTrackOverflow();
  }

  /* ── Constraint : limit track to max_visible items ── */

  _applyMaxVisible() {
    const maxVisible = parseInt(this.section.dataset.maxVisible || '6', 10);
    const items = [...this.track.querySelectorAll('.stories__pill, .stories__card')];

    if (items.length <= maxVisible) return; // no constraint needed

    const apply = () => {
      const item = items[0];
      if (!item || !item.offsetWidth) return; // not rendered yet

      const styles   = getComputedStyle(this.track);
      const gapPx    = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 20;
      // Peek : ~30 % de l'item suivant visible pour indiquer le scroll
      const peekW    = item.offsetWidth * 0.3;
      const maxWidth = maxVisible * item.offsetWidth + (maxVisible - 1) * gapPx + peekW;

      this.track.style.maxWidth      = maxWidth + 'px';
      this.track.style.justifyContent = 'flex-start';

      // Contraindre la track-wrap à track + 2 flèches + 2 gaps + padding
      // → les flèches restent naturellement collées aux bords du track
      const trackWrap   = this.track.parentElement;
      const arrowSize   = this.trackPrev?.offsetWidth || 36;
      const wrapGap     = parseFloat(getComputedStyle(trackWrap).gap) || 8;
      const wrapPadding = parseFloat(getComputedStyle(trackWrap).paddingLeft || '0') * 2;
      trackWrap.style.maxWidth    = (maxWidth + 2 * arrowSize + 2 * wrapGap + wrapPadding) + 'px';
      trackWrap.style.marginLeft  = 'auto';
      trackWrap.style.marginRight = 'auto';

      this._updateTrackArrows();
      // Recalcule le thumb maintenant que maxWidth est fixé
      this._updateProgressThumb();
    };

    // First try immediately (customizer / fast render)
    requestAnimationFrame(() => {
      apply();
      // Second pass after images/fonts load
      if (!items[0].offsetWidth) {
        setTimeout(apply, 300);
      }
    });
  }

  /* ── Public API ── */

  open(index = 0) {
    this.isOpen  = true;
    this.current = index;
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this._showSlide(index, null);
    this.modal.focus?.();
  }

  close() {
    this._pauseCurrent();
    clearTimeout(this.timer);
    this.isOpen = false;
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    // Return focus to the pill that opened this
    this.pills[this.current]?.focus();
  }

  goTo(index, direction = null) {
    if (index < 0 || index >= this.total) return;
    this._pauseCurrent();
    const prev = this.current;
    this.current = index;
    this._showSlide(index, direction ?? (index > prev ? 'next' : 'prev'));
  }

  next() { this.current < this.total - 1 ? this.goTo(this.current + 1, 'next') : this.close(); }
  prev() { this.current > 0              ? this.goTo(this.current - 1, 'prev') : null; }

  /* ── Private ── */

  _applySettings() {
    const size = this.section.dataset.circleSize || 'md';
    this.section.dataset.circleSize = size;

    // ring style — pills
    const ringStyle = this.section.dataset.ringStyle || 'gradient';
    this.section.querySelectorAll('.stories__ring').forEach(r => {
      r.classList.toggle('stories__ring--solid',    ringStyle === 'solid');
      r.classList.toggle('stories__ring--none',     ringStyle === 'none');
    });
    // ring style — cards
    this.section.querySelectorAll('.stories__card').forEach(c => {
      c.classList.toggle('stories__card--ring-solid', ringStyle === 'solid');
      c.classList.toggle('stories__card--ring-none',  ringStyle === 'none');
    });

    // label visibility
    if (this.section.dataset.showLabel === 'false') {
      this.section.classList.add('stories--no-label');
    }
  }

  _showSlide(index, direction) {
    // Hide all slides
    this.slides.forEach(s => {
      s.classList.remove('is-active', 'slide-in-up', 'slide-in-down');
      s.setAttribute('aria-hidden', 'true');
    });

    const slide = this.slides[index];
    slide.setAttribute('aria-hidden', 'false');

    // Mobile: directional animation (TikTok feel)
    if (!this.reducedMotion && direction && window.innerWidth <= 479) {
      slide.classList.add(direction === 'next' ? 'slide-in-up' : 'slide-in-down');
    }

    slide.classList.add('is-active');
    this._updateProgress(index);
    this._playCurrent();
  }

  /* Charge la vraie source vidéo au premier play (preload="none" par
     défaut — cf. audit perf sept. 2026 : 5 vidéos stories entièrement
     bufferisées dans une modale fermée). Idempotent : ne fait rien si
     déjà chargée. */
  _ensureVideoLoaded(video) {
    const deferredSources = video.querySelectorAll('source[data-src]');
    if (deferredSources.length) {
      deferredSources.forEach(s => {
        s.setAttribute('src', s.dataset.src);
        delete s.dataset.src;
      });
      video.load();
    } else if (video.dataset.src) {
      video.setAttribute('src', video.dataset.src);
      delete video.dataset.src;
      video.load();
    }
  }

  _playCurrent() {
    const slide = this.slides[this.current];
    const video = slide.querySelector('[data-story-video]');

    if (video) {
      this._ensureVideoLoaded(video);
      video.currentTime = 0;
      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => {});

      // Wait for metadata to get real duration
      if (isNaN(video.duration) || video.duration === 0) {
        video.addEventListener('loadedmetadata', () => this._startAutoAdvance(video.duration), { once: true });
      } else {
        this._startAutoAdvance(video.duration);
      }
    } else {
      // Image / no video: use fallback duration
      this._startAutoAdvance(this.fallbackMs / 1000);
    }
  }

  _startAutoAdvance(durationSec) {
    clearTimeout(this.timer);
    if (!this.paused) {
      this.timer = setTimeout(() => this.next(), durationSec * 1000);
    }
  }

  _pauseCurrent() {
    clearTimeout(this.timer);
    const slide = this.slides[this.current];
    const video = slide?.querySelector('[data-story-video]');
    if (video && !video.paused) video.pause();
  }

  _updateProgress(activeIndex) {
    this.fills.forEach((fill, i) => {
      fill.style.animation = 'none';
      fill.offsetHeight; // reflow

      if (i < activeIndex) {
        fill.style.width = '100%';
      } else if (i === activeIndex) {
        const slide = this.slides[i];
        const video = slide.querySelector('[data-story-video]');
        const applyDuration = (durSec) => {
          fill.style.width = '0%';
          fill.style.animation = `story-progress ${durSec}s linear forwards`;
        };

        if (video && (isNaN(video.duration) || video.duration === 0)) {
          // Durée pas encore connue : la vidéo se charge à la demande
          // (preload="none", cf. audit perf sept. 2026) donc les
          // métadonnées n'existent pas encore à ce stade. Fallback
          // immédiat, corrigé dès que loadedmetadata arrive.
          applyDuration(this.fallbackMs / 1000);
          video.addEventListener('loadedmetadata', () => {
            if (this.current === i) applyDuration(video.duration);
          }, { once: true });
        } else {
          const dur = (video && !isNaN(video.duration) && video.duration > 0)
            ? video.duration
            : this.fallbackMs / 1000;
          applyDuration(dur);
        }
      } else {
        fill.style.width = '0%';
      }
    });

    // Mark pill as watched
    this.pills[activeIndex]?.classList.add('is-watched');
  }

  _bindPills() {
    this.pills.forEach(pill => {
      pill.addEventListener('click', () => {
        this.open(parseInt(pill.dataset.storyIndex, 10));
      });
    });
  }

  _bindModal() {
    // Close via backdrop or close button
    this.modal.querySelectorAll('[data-stories-close]').forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    // Tap zones
    this.modal.querySelector('[data-stories-prev]')?.addEventListener('click', () => this.prev());
    this.modal.querySelector('[data-stories-next]')?.addEventListener('click', () => this.next());

    // Pause on hold (long press) — like Instagram
    let holdTimer = null;
    this.modal.addEventListener('pointerdown', () => {
      holdTimer = setTimeout(() => {
        this.paused = true;
        this._pauseCurrent();
      }, 200);
    });
    this.modal.addEventListener('pointerup', () => {
      clearTimeout(holdTimer);
      if (this.paused) {
        this.paused = false;
        this._playCurrent();
      }
    });
  }

  _bindTrackNav() {
    const scrollAmount = () => {
      const item = this.track.querySelector('.stories__pill, .stories__card');
      return item ? item.offsetWidth + 20 : 110;
    };

    this.trackPrev?.addEventListener('click', () => {
      this.track.scrollBy({ left: -scrollAmount() * 2, behavior: 'smooth' });
    });
    this.trackNext?.addEventListener('click', () => {
      this.track.scrollBy({ left: scrollAmount() * 2, behavior: 'smooth' });
    });

    // Click sur le liseré → sauter à la position
    this.progressEl?.addEventListener('click', (e) => {
      const rect  = this.progressEl.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      this.track.scrollLeft = ratio * this.track.scrollWidth;
    });
  }

  _updateProgressThumb() {
    if (!this.progressThumb) return;
    // Aligne la barre exactement sous le track (largeur + position horizontale)
    if (this.progressEl) {
      const trackW = this.track.offsetWidth;
      if (trackW > 0) {
        const trackRect  = this.track.getBoundingClientRect();
        const parentRect = this.progressEl.parentElement.getBoundingClientRect();
        this.progressEl.style.width      = trackW + 'px';
        this.progressEl.style.marginLeft = (trackRect.left - parentRect.left) + 'px';
      }
    }
    const ratio  = this.track.clientWidth / this.track.scrollWidth;
    const thumbW = Math.max(ratio * 100, 8);
    const thumbL = (this.track.scrollLeft / this.track.scrollWidth) * 100;
    this.progressThumb.style.width = thumbW + '%';
    this.progressThumb.style.left  = thumbL + '%';
  }

  _checkTrackOverflow() {
    const update = () => { this._updateTrackArrows(); this._updateProgressThumb(); };
    this.track.addEventListener('scroll', update, { passive: true });
    update();

    // Deuxième passe après que _applyMaxVisible (rAF async) a fixé le maxWidth
    requestAnimationFrame(() => requestAnimationFrame(() => update()));

    let resizeTimer;
    new ResizeObserver(() => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.track.style.maxWidth = '';
        this.track.parentElement.style.maxWidth = '';
        this.track.parentElement.style.marginLeft  = '';
        this.track.parentElement.style.marginRight = '';
        this._applyMaxVisible();
        this._updateTrackArrows();
        this._updateProgressThumb();
      }, 100);
    }).observe(this.section);
  }

  _updateTrackArrows() {
    const maxVisible = parseInt(this.section.dataset.maxVisible || '6', 10);
    const itemCount  = this.pills.length;
    const physicalOverflow = this.track.scrollWidth > this.track.clientWidth + 4;
    const needsNav = itemCount > maxVisible || physicalOverflow;

    // Garde-fou alignement : un défilement réellement nécessaire force
    // .stories__track à repasser en flex-start (cf. section-stories.css),
    // quel que soit l'alignement choisi dans le customizer — centrer/aligner
    // à droite une piste scrollable casse l'affordance (et le scroll tactile
    // sur certains mobiles).
    this.track.classList.toggle('is-overflowing', physicalOverflow);

    if (!needsNav) {
      this.trackPrev?.classList.add('stories__nav--hidden');
      this.trackNext?.classList.add('stories__nav--hidden');
      return;
    }

    /* Smart arrows : masquer la flèche quand on est en bout de piste */
    const atStart = this.track.scrollLeft <= 2;
    const atEnd   = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    this.trackPrev?.classList.toggle('stories__nav--hidden', atStart);
    this.trackNext?.classList.toggle('stories__nav--hidden', atEnd);
  }

  _bindSwipe() {
    let startX = 0;
    let startY = 0;

    this.modal.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    this.modal.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // Ignore micro-moves (taps)
      if (absDx < 20 && absDy < 20) return;

      if (absDy > absDx && absDy > 40) {
        // Vertical swipe (TikTok) — primary on mobile
        dy < 0 ? this.next() : this.prev();
      } else if (absDx > 40) {
        // Horizontal swipe — fallback
        dx < 0 ? this.next() : this.prev();
      }
    }, { passive: true });
  }

  _bindKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      switch (e.key) {
        case 'Escape':     this.close(); break;
        case 'ArrowRight': this.next();  break;
        case 'ArrowLeft':  this.prev();  break;
        case ' ':          e.preventDefault(); this._togglePause(); break;
      }
    });
  }

  _togglePause() {
    this.paused = !this.paused;
    this.paused ? this._pauseCurrent() : this._playCurrent();
  }
}

/* ── Init all Stories sections ── */
function initStories() {
  document.querySelectorAll('[data-stories-section]').forEach(el => {
    if (!el._storiesPlayer) {
      el._storiesPlayer = new StoriesPlayer(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStories);
} else {
  initStories();
}

/* Shopify theme editor: re-init on section reload */
document.addEventListener('shopify:section:load', (e) => {
  const el = e.target.querySelector('[data-stories-section]');
  if (el) el._storiesPlayer = new StoriesPlayer(el);
});
